import { Component, ojs, app, state, v, each } from "modular-openscriptjs";
import { pageInfo, formatThousand, formatDateForHumans, either, scrollToTop } from "../../../helpers";
import { api } from "../../../declarations";
import SolidIcon from "../../Blocks/SolidIcon";
import ProductView from "../../Views/Order/ProductView";
import OrderProductView from "../../Views/Order/OrderProductView";
import ServiceView from "../../Views/Order/ServiceView";

const h = app("h");

ojs(SolidIcon, ProductView, OrderProductView, ServiceView);

export default class OrderDetails extends Component {
    orderId = null;
    order = state(null);
    loading = state(true);

    render() {
        const router = app("router");
        this.orderId = router.params.orderId;
        scrollToTop();

        if (this.loading.value) {
            this.fetchOrder();
        }

        return h.div(
            { class: "min-vh-100 py-4 mb-5 py-md-5" },
            h.div(
                { class: "container" },
                h.div(
                    { class: "mb-4 d-flex align-items-center gap-3" },
                    h.button(
                        { class: "btn btn-white border shadow-sm rounded-circle p-2", onclick: "history.back()" },
                        h.SolidIcon("arrow-left")
                    ),
                    h.h2({ class: "fw-bold mb-0" }, "Order Details")
                ),

                v(this.order, (o) => {
                    if (!o.value) return h.div({ class: "text-center py-5" }, h.Spinner());

                    return h.div(
                        { class: "row g-4" },
                        // Info Sidebar
                        h.div(
                            { class: "col-12 col-lg-4" },
                            h.div(
                                { class: "card border-0 shadow-sm rounded-4 mb-4" },
                                h.div(
                                    { class: "card-body p-4" },
                                    h.h5({ class: "fw-bold mb-4" }, "Summary"),
                                    this.renderInfoItem("Order Code", `#${o.value.code}`),
                                    this.renderInfoItem("Date", formatDateForHumans(o.value.created_at)),
                                    this.renderInfoItem("Status", o.value.status.toUpperCase(), this.getStatusColor(o.value.status)),
                                    h.hr({ class: "my-4 opacity-10" }),
                                    this.renderInfoItem("Customer", o.value.user?.name || "Unknown"),
                                    this.renderInfoItem("Phone", o.value.user?.phone || "N/A"),
                                    h.hr({ class: "my-4 opacity-10" }),
                                    this.renderInfoItem("Deliverer", o.value.current_deliverer?.name || "Not Assigned", "primary"),
                                    this.renderInfoItem("Total Price", `KES ${formatThousand(o.value.total_price)}`, "success", "fs-4 fw-bold")
                                )
                            )
                        ),

                        // Items List
                        h.div(
                            { class: "col-12 col-lg-8" },
                            h.div(
                                { class: "card border-0 shadow-sm rounded-4" },
                                h.div(
                                    { class: "card-body p-4" },
                                    h.h5({ class: "fw-bold mb-4" }, "Order Items"),
                                    h.div(
                                        { class: "d-grid gap-3" },
                                        ...o.value.items.map(item => this.renderOrderItem(item))
                                    )
                                )
                            )
                        )
                    );
                })
            )
        );
    }

    renderInfoItem(label, value, color = "dark", extraClass = "") {
        return h.div(
            { class: "mb-3" },
            h.div({ class: "small text-muted text-uppercase fw-bold mb-1" }, label),
            h.div({ class: `text-${color} ${extraClass}` }, value)
        );
    }

    renderOrderItem(item) {
        const statuses = ['queued', 'processing', 'ready', 'picked up'];
        let itemView = h.empty();

        if (item.buyable_type === "App\\Models\\Product" || item.buyable_type === "Product") {
            itemView = h.ProductView(item, this.order.value);
        } else if (item.buyable_type === "App\\Models\\OrderProduct" || item.buyable_type === "OrderProduct") {
            itemView = h.OrderProductView(item, this.order.value);
        } else if (item.buyable_type === "App\\Models\\Service" || item.buyable_type === "Service") {
            itemView = h.ServiceView(item, this.order.value);
        } else {
            itemView = h.ProductView(item, this.order.value);
        }
        
        return h.div(
            { class: "p-3 border rounded-4 bg-white" },
            h.div(
                { class: "d-flex flex-column gap-3" },
                itemView,
                h.div(
                    { class: "d-flex justify-content-between align-items-center flex-wrap pt-2 border-top gap-2" },
                    h.small({ class: "text-muted" }, `Manage Status for this item:`),
                    h.div(
                        { class: "btn-group btn-group-sm shadow-sm rounded-pill overflow-hidden border" },
                        ...statuses.map(status => h.button(
                            { 
                                class: `btn border-0 py-2 px-3 ${item.status === status ? 'btn-primary' : 'btn-light'}`,
                                onclick: this.method("updateItemStatus", [item, status])
                            },
                            status.toUpperCase()
                        ))
                    )
                )
            )
        );
    }

    getStatusColor(status) {
        switch (status) {
            case 'completed': return 'success';
            case 'pending': return 'warning';
            case 'failed': return 'danger';
            case 'delivering': return 'info';
            default: return 'primary';
        }
    }

    async fetchOrder() {
        try {
            const companyId = window.currentCompany?.id;
            const res = await api.get(`companies/${companyId}/orders/${this.orderId}`);
            if (res) {
                this.order.value = res.data.order;
            }
        } catch (e) {
            console.error(e);
        } finally {
            this.loading.value = false;
        }
    }

    async updateItemStatus(item, newStatus) {
        if (item.status === newStatus) return;
        
        const oldStatus = item.status;
        item.status = newStatus;
        this.order.update(); // Trigger re-render

        try {
            const res = await api.put(`order-items/${item.id}/status`, { status: newStatus });
            if (!res) {
                item.status = oldStatus;
                this.order.update();
                app("inform")("Failed to update item status", "error");
            } else {
                app("inform")(`Item marked as ${newStatus}`, "success");
            }
        } catch (e) {
            item.status = oldStatus;
            this.order.update();
            app("inform")("Failed to update item status", "error");
        }
    }
}

ojs(OrderDetails);
